import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ChevronDown, ChevronUp, FileText, CheckCircle, Briefcase, DollarSign, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const WorkflowDiagram = ({ currentStage = 'quote' }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showStageInfo, setShowStageInfo] = useState(null);

  const stages = {
    quote: {
      icon: FileText,
      label: 'Quote',
      statuses: ['Draft', 'Sent', 'Accepted', 'Declined'],
      description: 'Create and send quotes to customers. Quotes can be edited, sent, accepted, or declined at any time.',
      color: 'blue'
    },
    order: {
      icon: CheckCircle,
      label: 'Order',
      statuses: ['Active', 'On Hold', 'Cancelled'],
      description: 'Accepted quotes become orders. Orders can be put on hold or cancelled if needed.',
      color: 'green'
    },
    job: {
      icon: Briefcase,
      label: 'Job',
      statuses: ['Unscheduled', 'Scheduled', 'In Progress', 'Completed'],
      description: 'Jobs are the actual work to be performed. Track progress from scheduling to completion.',
      color: 'purple'
    },
    payment: {
      icon: DollarSign,
      label: 'Payment',
      statuses: ['Invoiced', 'Paid'],
      description: 'Generate invoices and track payments after job completion.',
      color: 'amber'
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      green: 'bg-green-100 text-green-700 border-green-300',
      purple: 'bg-purple-100 text-purple-700 border-purple-300',
      amber: 'bg-amber-100 text-amber-700 border-amber-300'
    };
    return colors[stages[stage]?.color] || colors.blue;
  };

  return (
    <Card className="mb-6 overflow-hidden">
      <div 
        className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 cursor-pointer flex items-center justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Workflow Overview</h3>
          <span className="text-xs text-gray-600 bg-white px-2 py-1 rounded">
            All statuses are reversible
          </span>
        </div>
        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </div>

      {isExpanded && (
        <div className="p-6">
          <div className="flex items-center justify-between gap-4">
            {Object.keys(stages).map((stageKey, index) => {
              const stage = stages[stageKey];
              const Icon = stage.icon;
              const isActive = currentStage === stageKey;
              
              return (
                <React.Fragment key={stageKey}>
                  <div 
                    className={`flex-1 relative cursor-pointer transition-all ${
                      isActive ? 'scale-105' : 'opacity-70 hover:opacity-100'
                    }`}
                    onClick={() => setShowStageInfo(stageKey)}
                  >
                    <div className={`
                      border-2 rounded-lg p-4 text-center
                      ${isActive ? getStageColor(stageKey) + ' shadow-lg' : 'bg-gray-50 border-gray-300'}
                    `}>
                      <Icon className={`w-8 h-8 mx-auto mb-2 ${isActive ? '' : 'text-gray-500'}`} />
                      <div className="font-semibold mb-1">{stage.label}</div>
                      <div className="text-xs text-gray-600">
                        {stage.statuses.length} statuses
                      </div>
                    </div>
                    {isActive && (
                      <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                        Current
                      </div>
                    )}
                  </div>
                  
                  {index < Object.keys(stages).length - 1 && (
                    <div className="flex items-center gap-1 text-gray-400">
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                      <span className="text-xl">→</span>
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="mt-6 flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-600">✓ All statuses are reversible</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-gray-600">Click any stage to learn more</span>
            </div>
          </div>
        </div>
      )}

      {/* Stage Info Dialog */}
      <Dialog open={!!showStageInfo} onOpenChange={() => setShowStageInfo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {showStageInfo && React.createElement(stages[showStageInfo].icon, { className: "w-5 h-5" })}
              {showStageInfo && stages[showStageInfo].label} Stage
            </DialogTitle>
          </DialogHeader>
          {showStageInfo && (
            <div className="space-y-4">
              <p className="text-gray-700">{stages[showStageInfo].description}</p>
              
              <div>
                <h4 className="font-semibold mb-2">Available Statuses:</h4>
                <div className="flex flex-wrap gap-2">
                  {stages[showStageInfo].statuses.map(status => (
                    <span 
                      key={status}
                      className={`px-3 py-1 rounded text-sm ${getStageColor(showStageInfo)}`}
                    >
                      {status}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                <p className="font-semibold text-blue-900 mb-1">💡 Tip:</p>
                <p className="text-blue-800">
                  You can change between any status at any time. All changes are tracked in the status history.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default WorkflowDiagram;
